import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushRect, pushSegment } from '../glyphs/frame';
import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import { t } from '../../translation/markers';

/** Segments approximating the bowl ellipse. */
const BOWL_SEGMENTS = 20;

/**
 * Toilet: a fixed-size wall fixture (POINT anchor + WALL constraint) — a
 * single click snaps it flush against the nearest wall face and it slides
 * along the wall on drag. Drawn parametrically: cistern box against the wall
 * (local -Y) plus an elliptical bowl toward the room, with a seat line
 * between them.
 */
function buildToiletGlyph(frame: SketchObjectFrame): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;

  // Cistern against the wall: full width, ~30% of the depth.
  const cisternFront = -hd + frame.depth * 0.3;
  pushRect(out, frame, -hw, hw, -hd, cisternFront);

  // Seat hinge line at the cistern front, slightly inset from the sides.
  pushSegment(out, at(frame, -hw * 0.8, cisternFront), at(frame, hw * 0.8, cisternFront), frame.z);

  // Bowl: an ellipse filling the remaining depth, slightly narrower than the
  // cistern.
  const bowlRadiusU = hw * 0.75;
  const bowlCenterN = (cisternFront + hd) / 2;
  const bowlRadiusN = (hd - cisternFront) / 2;
  let prev: Pt2 | null = null;
  for (let i = 0; i <= BOWL_SEGMENTS; i++) {
    const t = (2 * Math.PI * i) / BOWL_SEGMENTS;
    const pt = at(frame, bowlRadiusU * Math.cos(t), bowlCenterN + bowlRadiusN * Math.sin(t));
    if (prev) pushSegment(out, prev, pt, frame.z);
    prev = pt;
  }

  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'toilet',
    label: t('Toilet'),
    category: SketchObjectCategory.BATHROOM,
    anchor: SketchObjectAnchorKind.POINT,
    constraint: { kind: 'WALL', defaultInset: 0 },
    defaultSize: { width: 0.4, depth: 0.7 },
    params: {},
    glyph: { kind: 'parametric', build: buildToiletGlyph },
  }),
);
